import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { connect } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { withParams } from 'providers/ParamsConsumer';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import {
  shape,
  required,
  email,
  when,
  fromModel,
  Validator,
  Failure,
  Success,
  ValidationError,
  stopOnFirstFailure
} from '@totalsoft/pure-validations';
import { isCNP, isCIF } from '@totalsoft/validations';
import { isMobilePhoneByCountry } from 'utils/pure-validation/primitiveValidators';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { createOfferFromSimulator, createKycSession } from '../../actionCreators';
import { initialPartner } from '../../constants';
import SimulatorAssetComponent from './SimulatorAssetComponent';
import SimulatorClientContainer from './SimulatorClientContainer';
import SimulatorFinancialProductsContainer from './SimulatorFinancialProductsContainer';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';

const SimulatorOfferContainer = ({ t, actions, params, savingOffer, assetInfo, handleAssetInfoFiltersChange }) => {
  const { useKycIntegration } = useContext(ApplicationFeaturesContext);

  const validator = (pFTypeId, pJTypeId, t) => {
    const cnp = Validator(function cnp(content) {
      return isCNP(content) ? Success : Failure(ValidationError(t('Validations.Generic.CNP')));
    });

    const cif = Validator(function cif(content) {
      return isCIF(content) ? Success : Failure(ValidationError(t('Validations.Generic.RegistrationCode')));
    });

    return fromModel(item =>
      shape({
        generalData: shape({
          uniqueId:
            [required, cnp |> when(item.partnerType.partnerTypeId === pFTypeId), cif |> when(item.partnerType.partnerTypeId === pJTypeId)]
            |> stopOnFirstFailure,
          firstName: required |> when(item.partnerType.partnerTypeId === pFTypeId && item.generalData.uniqueId !== ''),
          lastName: required |> when(item.partnerType.partnerTypeId === pFTypeId && item.generalData.uniqueId !== ''),
          partnerName: required |> when(item.partnerType.partnerTypeId === pJTypeId && item.generalData.uniqueId !== '')
        }),
        contactData: shape({
          email: email,
          phone: isMobilePhoneByCountry(params.countryCode)
        }),
        partnerType: shape({
          partnerTypeId: required |> when(item.generalData.firstName !== '') |> when(item.generalData.uniqueId !== '')
        })
      })
    );
  };

  const [partnerLens, partnerDirtyInfo, resetPartnerLens] = useChangeTrackingLens({
    ...initialPartner,
    partnerType: { partnerTypeId: params.pFTypeId, typeCheck: { isPF: true } }
  });
  const [savedOfferData, setSavedOfferData] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [completed, setCompleted] = useState({ offer: false, kyc: false });

  const partnerValidator = useMemo(
    () => validator(params.pFTypeId, params.pJTypeId, t),
    [params.pFTypeId, params.pJTypeId, t] //eslint-disable-line react-hooks/exhaustive-deps
  );
  const [validation, validate] = useDirtyFieldValidation(partnerValidator);

  const canGenerateContract = useMemo(() => {
    return (partnerLens.partnerType.partnerTypeId |> get) === params.pFTypeId && useKycIntegration;
  }, [partnerLens.partnerType.partnerTypeId, useKycIntegration, params]);

  const editPartnerDisabled = useMemo(() => completed.offer || completed.kyc || savingOffer, [completed, savingOffer]);

  useEffect(() => {
    validate(partnerLens |> get, partnerDirtyInfo);
  }, [validate, partnerLens, partnerDirtyInfo]);

  const handlePartnerTypeChange = partnerType => {
    resetPartnerLens({ ...initialPartner, partnerType });
  };

  const handleCreate = useCallback(
    (product, requireKyc = false) => {
      const offerData = {
        assetHierarchyId: assetInfo.assetHierarchyId,
        duration: assetInfo.financingPeriod,
        advanceAmount: assetInfo.advance,
        listPrice: assetInfo.listPrice,
        client: partnerLens |> get,
        productId: product.productId,
        requireKyc,
        dealerId: product.dealerId,
        assetProperties: product.assetProperties
      };

      if (!validate(partnerLens |> get)) return;

      if (requireKyc && savedOfferData) {
        const kycData = {
          documentId: savedOfferData.documentId,
          siteId: savedOfferData.siteId,
          uniqueId: offerData.client.generalData.uniqueId,
          firstName: offerData.client.generalData.firstName,
          lastName: offerData.client.generalData.lastName,
          email: offerData.client.contactData.email,
          phone: offerData.client.contactData.phone
        };
        actions.createKycSession(kycData, _ => {
          setCompleted(prev => ({ ...prev, kyc: true }));
          setSelectedProductId(product.productId);
        });
      } else {
        actions.createOfferFromSimulator(offerData, data => {
          setSavedOfferData({ documentId: data.documentId, siteId: data.siteId });
          setCompleted({ offer: true, kyc: requireKyc });
          setSelectedProductId(product.productId);
          window.open(`/offers/${data.documentId}/${data.siteId}`, '_blank');
        });
      }
    },
    [
      actions,
      assetInfo.advance,
      assetInfo.assetHierarchyId,
      assetInfo.financingPeriod,
      assetInfo.listPrice,
      partnerLens,
      savedOfferData,
      validate
    ]
  );
  const _handleCreate = useCallback(product => handleCreate(product, true), [handleCreate]);

  return (
    <Grid container spacing={1} sx={{ width: '100%', margin: '0 auto' }}>
      <div style={{ width: '100%', marginTop: '15px' }}>
        <Grid container spacing={3}>
          <SimulatorAssetComponent assetInfo={assetInfo} handleAssetInfoFiltersChange={handleAssetInfoFiltersChange} />
          <SimulatorClientContainer
            partnerLens={partnerLens}
            validation={validation}
            editPartnerDisabled={editPartnerDisabled}
            onPartnerTypeChange={handlePartnerTypeChange}
          />
        </Grid>
      </div>
      <SimulatorFinancialProductsContainer
        assetInfo={assetInfo}
        partnerTypeId={partnerLens.partnerType.partnerTypeId |> get}
        partnerAgreesWithPersonalDataUse={partnerLens.agreesWithPersonalDataUse |> get}
        savingOffer={savingOffer}
        completed={completed}
        selectedProductId={selectedProductId}
        canGenerateContract={canGenerateContract}
        onGenerateOffer={handleCreate}
        onGenerateContract={_handleCreate}
      />
    </Grid>
  );
};

SimulatorOfferContainer.propTypes = {
  assetInfo: PropTypes.object.isRequired,
  savingOffer: PropTypes.bool,
  t: PropTypes.func.isRequired,
  partnerTypes: PropTypes.array,
  params: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  relations: PropTypes.array,
  gdpr: PropTypes.object,
  handleAssetInfoFiltersChange: PropTypes.func.isRequired
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createOfferFromSimulator,
        createKycSession
      },
      dispatch
    )
  };
}

function mapStateToProps() {
  return {};
}
export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(withParams(SimulatorOfferContainer));
